import type { PageTranslator } from '../types';
import React, { useEffect, useRef, useState } from 'react';
import Board from './PageBoard';
import { Trans, useTranslation } from 'react-i18next';
import GeneralQuestions from './FaqGeneralQuestions';
import CircleQuestions from './FaqTribesQuestions';
import FoundationQuestions from './FaqFoundationQuestions';
import TechnologyQuestions from './FaqTechnologyQuestions';
import BugsQuestions from './FaqBugsAndFeaturesQuestions';

const FAQ_CATEGORIES = [
  { key: 'general', Questions: GeneralQuestions },
  { key: 'circles', Questions: CircleQuestions },
  { key: 'foundation', Questions: FoundationQuestions },
  { key: 'technology', Questions: TechnologyQuestions },
  {
    key: 'bugs-and-features',
    Questions: BugsQuestions,
  },
];

interface FaqProps {
  category?:
    | 'bugs-and-features'
    | 'circles'
    | 'foundation'
    | 'general'
    | 'technology'
    | 'tribes';
  children?: React.ReactNode;
}

export default function Faq({ category, children }: FaqProps) {
  const { t: rawT } = useTranslation('pages');
  const t = rawT as unknown as PageTranslator;
  const questionsRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('');
  const [hasMatches, setHasMatches] = useState(true);
  const selectedCategory =
    category === 'tribes' ? 'circles' : category || 'general';
  const categoryLabels: Record<string, string> = {
    general: t('Site & community'),
    circles: t('Circles'),
    foundation: t('Foundation'),
    technology: t('Technology'),
    'bugs-and-features': t('Bugs & Features'),
  };

  useEffect(() => {
    const query = filter.trim().toLocaleLowerCase();
    const questions =
      questionsRef.current?.querySelectorAll<HTMLElement>('.faq-question');
    let matchCount = 0;

    questions?.forEach(question => {
      const matches =
        !query || question.textContent?.toLocaleLowerCase().includes(query);
      question.hidden = !matches;
      if (matches) matchCount += 1;
    });

    questionsRef.current
      ?.querySelectorAll<HTMLElement>('.faq-results-category')
      .forEach(group => {
        group.hidden = query
          ? !group.querySelector('.faq-question:not([hidden])')
          : group.dataset.category !== selectedCategory;
      });

    setHasMatches(!query || matchCount > 0);
  }, [children, filter, selectedCategory]);

  return (
    <>
      <Board className="faq-header" names={['happyhippies', 'guitarcamper']}>
        <div className="container">
          <div className="row">
            <div className="col-xs-12 text-center">
              <h2 className="visible-xs-block">{t('FAQ')}</h2>
              <h2 className="hidden-xs">{t('Frequently Asked Questions')}</h2>
              {category === 'general' && (
                <h3>{t('about the site & community')}</h3>
              )}
              {(category === 'tribes' || category === 'circles') && (
                <h3>{t('about circles')}</h3>
              )}
              {category === 'foundation' && (
                <h3>{t('about the foundation')}</h3>
              )}
              {category === 'technology' && <h3>{t('about technology')}</h3>}
              {category === 'bugs-and-features' && (
                <h3>{t('about Bugs & Features')}</h3>
              )}
            </div>
          </div>
        </div>
      </Board>

      <section className="container">
        <div className="row">
          <div className="col-xs-12 col-sm-4 col-md-4">
            <div id="faq-sidebar" className="sidebar">
              {/* <Site & Community */}
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a href="/faq" className="undecorated-link text-color">
                      {t('Site & community')}
                    </a>
                  </h3>
                </div>
                {category === 'general' && (
                  <div className="panel-body">
                    <div className="list-group">
                      <a
                        href="/faq#what-is-the-minimum-age"
                        className="list-group-item"
                      >
                        {t('What is the minimum age to use Trustroots?')}
                      </a>
                      <a
                        href="/faq#is-trustroots-exclusively-for-hitchhikers"
                        className="list-group-item"
                      >
                        {t('Is Trustroots exclusively for hitchhikers?')}
                      </a>
                      <a
                        href="/faq#what-is-your-long-term-vision"
                        className="list-group-item"
                      >
                        {t('What is your long term vision?')}
                      </a>
                      <a
                        href="/faq#is-trustroots-alternative"
                        className="list-group-item"
                      >
                        {t(
                          'Is Trustroots an alternative for CouchSurfing or BeWelcome?',
                        )}
                      </a>
                      <a
                        href="/faq#is-there-mobile-app"
                        className="list-group-item"
                      >
                        {t('Is there a mobile app?')}
                      </a>
                      <a
                        href="/faq#how-is-this-connected-to-hitchwiki-nomadwiki-or-trashwiki"
                        className="list-group-item"
                      >
                        {t(
                          'How is this connected to Hitchwiki, Nomadwiki or Trashwiki?',
                        )}
                      </a>
                      <a
                        href="/faq#why-the-name-trustroots"
                        className="list-group-item"
                      >
                        {t('Why the name Trustroots?')}
                      </a>
                      <a
                        href="/faq#what-happened-to-facebook-connections"
                        className="list-group-item"
                      >
                        {t('What happened to Facebook connections?')}
                      </a>
                      <a
                        href="/faq#why-is-there-no-forum"
                        className="list-group-item"
                      >
                        {t('Why is there no forum?')}
                      </a>
                      <a
                        href="/faq#why-was-my-account-suspended"
                        className="list-group-item"
                      >
                        {t('Why was my account suspended?')}
                      </a>
                      <a
                        href="/faq#how-can-i-remove-my-account"
                        className="list-group-item"
                      >
                        {t('How can I remove my account?')}
                      </a>
                      <a href="/faq#how-can-i-help" className="list-group-item">
                        {t('How can I help?')}
                      </a>
                      <a
                        href="/faq#how-can-i-contact-you"
                        className="list-group-item"
                      >
                        {t('How can I contact you?')}
                      </a>
                      <a
                        href="/faq#how-do-i-report-a-member-that-violates-the-rules"
                        className="list-group-item"
                      >
                        {t('How do I report a member that violates the rules?')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Foundation */}
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a
                      href="/faq/foundation"
                      className="undecorated-link text-color"
                    >
                      {t('Foundation')}
                    </a>
                  </h3>
                </div>
                {category === 'foundation' && (
                  <div className="panel-body">
                    <div className="list-group">
                      <a
                        href="/faq/foundation#what-is-your-legal-status"
                        className="list-group-item"
                      >
                        {t("What's your legal status?")}
                      </a>
                      <a
                        href="/faq/foundation#why-limited-by-guarantee"
                        className="list-group-item"
                      >
                        {t(
                          'Why Limited by Guarantee under section 60 exemption?',
                        )}
                      </a>
                      <a
                        href="/faq/foundation#are-you-going-to-sell-out"
                        className="list-group-item"
                      >
                        {t('Could Trustroots assets be sold?')}
                      </a>
                      <a
                        href="/faq/foundation#who-are-the-board"
                        className="list-group-item"
                      >
                        {t('Who are the board?')}
                      </a>
                      <a
                        href="/faq/foundation#how-do-you-want-to-make-the-project-financially-sustainable"
                        className="list-group-item"
                      >
                        {t(
                          'How do you want to make the project financially sustainable?',
                        )}
                      </a>
                      <a
                        href="/faq/foundation#who-decides-what-gets-done"
                        className="list-group-item"
                      >
                        {t('Who decides what gets done?')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Circles */}
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a
                      href="/faq/circles"
                      className="undecorated-link text-color"
                    >
                      {t('Circles')}
                    </a>
                  </h3>
                </div>
                {category === 'circles' && (
                  <div className="panel-body">
                    <div className="list-group">
                      <a
                        href="/faq/circles#what-are-circles"
                        className="list-group-item"
                      >
                        {t('What are circles?')}
                      </a>
                      <a
                        href="/faq/circles#no-suitable-circles"
                        className="list-group-item"
                      >
                        {t("I don't find a circle that suits me")}
                      </a>
                      <a
                        href="/faq/circles#tribes-rename-to-circles"
                        className="list-group-item"
                      >
                        {t('Why did you rename "tribes" to "circles"?')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Technology */}
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a
                      href="/faq/technology"
                      className="undecorated-link text-color"
                    >
                      {t('Technology')}
                    </a>
                  </h3>
                </div>
                {category === 'technology' && (
                  <div className="panel-body">
                    <div className="list-group">
                      <a
                        href="/faq/technology#opensource"
                        className="list-group-item"
                      >
                        {t('Is Trustroots open source?')}
                      </a>
                      <a
                        href="/faq/technology#im-a-developer"
                        className="list-group-item"
                      >
                        {t("I'm a developer and I want to help!")}
                      </a>
                      <a
                        href="/faq/technology#are-you-planning-to-do-x-feature"
                        className="list-group-item"
                      >
                        {t('Are you planning to do X feature?')}
                      </a>
                      <a
                        href="/faq/technology#statistics"
                        className="list-group-item"
                      >
                        {t('Do you have public statistics?')}
                      </a>
                      <a
                        href="/faq/technology#f-droid"
                        className="list-group-item"
                      >
                        {t('Could you offer mobile app at F-Droid store?')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Bugs & Features */}
              <div className="panel panel-default">
                <div className="panel-heading">
                  <h3 className="panel-title">
                    <a
                      href="/faq/bugs-and-features"
                      className="undecorated-link text-color"
                    >
                      {t('Bugs & Features')}
                    </a>
                  </h3>
                </div>
                {category === 'bugs-and-features' && (
                  <div className="panel-body">
                    <div className="list-group">
                      <a
                        href="/faq/bugs-and-features#how-do-i-report-a-bug"
                        className="list-group-item"
                      >
                        {t('How do I report a bug?')}
                      </a>
                      <a
                        href="/faq/bugs-and-features#where-can-i-suggest"
                        className="list-group-item"
                      >
                        {t(
                          'Where can I suggest an improvement or new feature?',
                        )}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* .sidebar */}
          </div>
          <div className="col-xs-12 col-sm-8 col-md-8">
            <div className="faq-filter form-group">
              <label htmlFor="faq-filter">
                {t('Search all frequently asked questions')}
              </label>
              <input
                id="faq-filter"
                type="search"
                className="form-control"
                value={filter}
                onChange={event => setFilter(event.target.value)}
              />
            </div>
            <div ref={questionsRef}>
              {FAQ_CATEGORIES.map(({ key, Questions }) => (
                <section
                  key={key}
                  className="faq-results-category"
                  data-category={key}
                  hidden={key !== selectedCategory && !filter.trim()}
                  aria-label={categoryLabels[key]}
                >
                  {filter.trim() && <h2>{categoryLabels[key]}</h2>}
                  {key === selectedCategory ? (
                    children
                  ) : filter.trim() ? (
                    <Questions />
                  ) : null}
                </section>
              ))}
            </div>
            {!hasMatches && (
              <p role="status">{t('No questions match your search.')}</p>
            )}
            <br />
            <br />
            <hr />
            <p className="lead text-center" id="more-questions">
              {/* @TODO remove ns (issue #1368) */}
              <Trans t={rawT} ns="pages">
                More questions? <a href="/support">Ask us!</a> <br />
                <br />
                See also our <a href="https://ideas.trustroots.org/">
                  blog
                </a>{' '}
                and <a href="/">about</a> page.
              </Trans>
            </p>
          </div>
        </div>
        {/* /.row */}
      </section>
      {/* /.container */}
    </>
  );
}
